// Expand an iReal Pro chart's notation into a linear performance order.
//
// Resolves one/two-bar repeats, then runs a small sequencer-style interpreter
// that follows repeat barlines, 1st/2nd/3rd endings (voltas), and the common
// navigation directives (D.C. / D.S. al Coda / al Fine, To Coda, Fine). The
// result is a flat list of measures with concrete chords, ready to drive
// diagrams, the voice-leading optimiser, and playback.

import { cloneMelody, type ChartMelodyNote } from '../chartMelody';
import { uid } from '../song';
import type { IRealChord, IRealChart, IRealMeasure } from './types';

const GUARD_LIMIT = 100000;
const NAVIGATION = /D\.\s*([SC])\./i;

function cloneChords(chords: IRealChord[]): IRealChord[] {
  return chords.map((c) => ({
    ...c,
    id: uid('c'),
    alternate: c.alternate ? { ...c.alternate } : c.alternate,
  }));
}

/** Melody copied from a simile's source bar (fresh ids), or absent. */
function copiedMelody(melody: ChartMelodyNote[] | undefined): { melody?: ChartMelodyNote[] } {
  return melody && melody.length > 0 ? { melody: cloneMelody(melody) } : { melody: undefined };
}

/** Replace one/two-bar repeat measures with copies of the preceding measure(s). */
export function resolveBarRepeats(measures: IRealMeasure[]): IRealMeasure[] {
  const out: IRealMeasure[] = [];
  for (let i = 0; i < measures.length; i++) {
    const m = measures[i];
    if (m.barRepeat && m.chords.length === 0) {
      const { barRepeat, ...rest } = m;
      void barRepeat;
      const twoBack = out[out.length - 2];
      const oneBack = out[out.length - 1];
      if (m.barRepeat === 2 && twoBack && oneBack) {
        // The two-bar simile stands for BOTH preceding bars, so it expands to
        // two measures: the sign bar's opening structure goes on the first
        // half, its closing structure (barline, directive) on the second.
        out.push({
          ...rest,
          close: undefined,
          directive: undefined,
          staffText: undefined,
          chords: cloneChords(twoBack.chords),
          ...copiedMelody(twoBack.melody),
        });
        out.push({
          ...rest,
          open: undefined,
          section: undefined,
          ending: undefined,
          coda: undefined,
          segno: undefined,
          timeSig: undefined,
          chords: cloneChords(oneBack.chords),
          ...copiedMelody(oneBack.melody),
        });
      } else {
        const src = oneBack;
        out.push({
          ...rest,
          chords: src ? cloneChords(src.chords) : [],
          ...copiedMelody(src?.melody),
        });
      }
    } else {
      out.push(m);
    }
  }
  return out;
}

/**
 * Written bars `from..to` (inclusive indices) as they SOUND inside the whole
 * form — the practice loop's slice. Cutting the bars out first loses what was
 * in force at the cut: a `%` at the start of the slice has nothing to copy, an
 * empty bar has no chord to carry, and the meter falls back to the chart's.
 * So similes resolve against the whole chart, and the first bar is given the
 * chord and meter it plays under. A chord is carried in only where one is
 * sounding: after an N.C., or in the form's leading blank bars, the bar stays
 * silent exactly as it does in the whole form.
 */
export function passageMeasures(
  measures: IRealMeasure[],
  from: number,
  to: number,
  defaultTimeSignature: [number, number],
): IRealMeasure[] {
  const resolved = resolveBarRepeats(measures);
  // A two-bar simile resolves to two bars sharing its id, so select by id.
  const ids = new Set(measures.slice(from, to + 1).map((m) => m.id));
  const start = resolved.findIndex((m) => ids.has(m.id));
  if (start < 0) return [];
  const slice = resolved.filter((m) => ids.has(m.id));

  let meter = defaultTimeSignature;
  let mark: IRealChord | undefined;
  for (const m of resolved.slice(0, start)) {
    meter = m.timeSig ?? meter;
    mark = m.chords.at(-1) ?? mark;
  }
  const first = slice[0];
  const firstMeter = first.timeSig ?? meter;
  const restated =
    first.timeSig || (meter[0] === defaultTimeSignature[0] && meter[1] === defaultTimeSignature[1])
      ? {}
      : { timeSig: meter };
  const carried =
    first.chords.length === 0 && mark?.chordType && !mark.noChord
      ? { chords: [{ ...cloneChords([mark])[0], beats: first.pickupBeats ?? firstMeter[0] }] }
      : {};
  return [{ ...first, ...restated, ...carried }, ...slice.slice(1)];
}

/**
 * How a repeat block beginning at `start` plays: how many passes, and the
 * pass that takes its last written bracket — the block ends there even when
 * an `<Nx>` asks for more passes than it has brackets.
 */
function passesFor(
  measures: IRealMeasure[],
  start: number,
  /** The first bar's bracket belongs to an enclosing repeat, not this one. */
  bracketOutside = false,
): Pick<RepeatFrame, 'passes' | 'lastBracket'> {
  let maxEnding = 0;
  let override = 0;
  let seenClose = false;
  for (let i = start; i < measures.length; i++) {
    const m = measures[i];
    if (i > start && m.open === 'repeat') break; // a new repeat block begins
    // Past the first `}` the block goes on only through the brackets its
    // repeat barlines lead into: a bar straight after a `}` opens the next
    // bracket or ends the block, and a multi-bar bracket runs to its own
    // barline (Looking Up's 8-bar 2nd ending closes with the `}` into its
    // 3rd). A bracket no `}` leads into is reached by navigation instead
    // (Cabaret's D.C. al 3rd ending) and is not a pass of the repeat.
    if (seenClose && (measures[i - 1].close === 'repeat') !== (m.ending != null)) break;
    if (m.ending != null && !(bracketOutside && i === start)) {
      maxEnding = Math.max(maxEnding, m.ending);
    }
    const sm = /(\d+)x/.exec(m.staffText ?? '');
    if (sm) override = Math.max(override, parseInt(sm[1], 10));
    if (m.close === 'repeat') seenClose = true;
    else if (seenClose && (m.close === 'double' || m.close === 'final')) break;
  }
  const lastBracket = Math.max(2, maxEnding);
  return { passes: override > 0 ? override : lastBracket, lastBracket };
}

interface RepeatFrame {
  start: number;
  count: number;
  passes: number;
  /** The pass whose bracket is the block's last written one. */
  lastBracket: number;
  /** The first bar is a bracket of an enclosing repeat, judged when the
   * frame opened: a pass that comes back to it plays it. */
  bracketOutside?: boolean;
}

/**
 * Where a closing repeat with no opening one returns to: the bar after the
 * previous closing repeat or final barline, else the top of the form — after
 * a leading pickup, as a D.S. with no segno does. -1 when that bar opens a
 * bracket: the `}` then closes the last ending of the repeat before it (I Feel
 * Pretty's `}N2 … }`), which is no repeat of its own.
 */
function implicitRepeatStart(measures: IRealMeasure[], close: number): number {
  let start = measures[0]?.pickupBeats != null ? 1 : 0;
  for (let i = close - 1; i >= 0; i--) {
    if (measures[i].close === 'repeat' || measures[i].close === 'final') {
      start = i + 1;
      break;
    }
  }
  return start > close || measures[start].ending != null ? -1 : start;
}

/** Skip a volta whose number does not match the current pass. */
function skipVolta(measures: IRealMeasure[], from: number, stack: RepeatFrame[]): number {
  let pc = from;
  while (pc < measures.length) {
    const m = measures[pc];
    if (m.close === 'repeat') {
      // Crossing the repeat barline exits the block — unless it leads into a
      // later bracket this pass can still reach: a three-ending repeat's 2nd
      // and 3rd passes cross the 1st bracket's `}` on the way to their own.
      const top = stack[stack.length - 1];
      const next = measures[pc + 1]?.ending;
      if (top && !(next != null && next <= top.count + 1)) stack.pop();
      return pc + 1;
    }
    pc++;
    const next = measures[pc];
    if (next && next.ending != null) return pc; // reached the next volta
  }
  return pc;
}

export interface FlattenOptions {
  /** Repeat the whole expanded form this many times (for looped playback). */
  wholeRepeats?: number;
  /** Keep a leading pickup only on the first whole-form pass. */
  pickupOnce?: boolean;
}

export interface PlaybackFormPlan {
  /** Chorus used for every non-final or infinite-loop pass. */
  loopPass: IRealMeasure[];
  /** Last pass, including the detached coda when the chart has one. */
  finalPass: IRealMeasure[];
  hasDetachedCoda: boolean;
  /** The chart carries iReal's `U` end marker and `finalPass` stops there. */
  endsAtMarker: boolean;
}

/**
 * Cut a performed sequence at iReal's `U` END MARKER: everything after the
 * LAST bar carrying the marker is dropped. Repeated bars keep their written
 * ids and flags through `flattenMeasures`, so the marker bar's final
 * occurrence is where the tune ends; without a marker the input returns as is.
 */
export function honoursEndMarker(measures: IRealMeasure[]): boolean {
  if (!measures.some((m) => m.endMarker)) return false;
  // A chart with real NAVIGATION (segno, coda, D.C., D.S.) owns its own ending
  // — "D.C. al 1st ending" charts (Goodbye, Pensativa, I Get A Kick Out Of
  // You) put the marker inside the first ending beside the Fine, and the
  // return pass takes the last alternative, so the marker bar never recurs
  // where the tune ends; cutting at its last occurrence would drop the whole
  // B section. A bare `Fine` text is not navigation. Corpus (Jazz 1460): 157
  // charts carry the marker, 36 of them navigation charts; of the rest every
  // marker sits on the form's last bar or within four bars of it, the
  // material after it being the turnaround or tag played only going round.
  return !measures.some((m) => m.segno || m.coda || /D\.\s*[SC]\./i.test(m.directive ?? ''));
}

/**
 * Does a bare outer |:…:| wrap the whole span? True only when the span's ONLY
 * repeat marks are an opening one on its first bar and/or a closing one on
 * its last, with no endings. An opening |: whose :| lands mid-span is an
 * interior vamp (Chameleon's two-bar intro figure), and interior repeats are
 * written form — never the whole-chorus declaration.
 */
export function bareOuterRepeat(bars: IRealMeasure[]): boolean {
  const last = bars.length - 1;
  if (last < 0) return false;
  if (bars.some((m) => m.ending != null)) return false;
  const interior = bars.some(
    (m, i) => (i > 0 && m.open === 'repeat') || (i < last && m.close === 'repeat'),
  );
  return !interior && (bars[0].open === 'repeat' || bars[last].close === 'repeat');
}

/** The written form with its outer |:…:| pair taken off, so it flattens once. */
function dropOuterRepeat(measures: IRealMeasure[]): IRealMeasure[] {
  const last = measures.length - 1;
  return measures.map((m, i) => {
    if (i === 0 && m.open === 'repeat') m = { ...m, open: undefined };
    if (i === last && m.close === 'repeat') m = { ...m, close: undefined };
    return m;
  });
}

export function cutAtEndMarker(measures: IRealMeasure[]): IRealMeasure[] {
  let last = -1;
  measures.forEach((m, i) => {
    if (m.endMarker) last = i;
  });
  return last < 0 ? measures : measures.slice(0, last + 1);
}

/** An explicit return such as "D.S., play ABEB (Coda)" names its route.
 * Repeated letters are intentional visits, not a contiguous range. Resolve
 * every letter before accepting the route; duplicate/unknown section labels
 * must not produce a partly guessed itinerary. */
function namedSectionReturn(measures: IRealMeasure[], directive: string): IRealMeasure[] | null {
  const match = /\bplay\s+([a-z]+)\b/i.exec(directive);
  if (!match) return null;
  const labels = [...match[1].toUpperCase()];
  const starts = labels.map((label) => measures.flatMap((m, i) =>
    m.section?.trim().toUpperCase() === label ? [i] : [],
  ));
  if (starts.some((matches) => matches.length !== 1)) return null;
  const codas = measures.flatMap((m, i) => m.coda ? [i] : []);
  const wantsCoda = /\bcoda\b/i.test(directive);
  const exit = codas[0] ?? -1;
  const target = codas.length > 1 ? codas.at(-1)! : -1;
  const ranges = starts.map(([start]) => {
    const next = measures.findIndex((m, i) => i > start && (!!m.section || i === target));
    return { start, end: next < 0 ? measures.length - 1 : next - 1 };
  });
  const last = ranges.at(-1)!;
  if (wantsCoda && (target < 0 || exit < last.start || exit > last.end)) return null;

  // Restore the written context at every section jump. The previous played
  // section may finish in a different meter or on a different harmony.
  const span = (start: number, end: number, asReturn: boolean) => {
    const bars = passageMeasures(measures, start, end, measures[0]?.timeSig ?? [4, 4])
      .map((m) => ({
        ...m,
        directive: NAVIGATION.test(m.directive ?? '') ? undefined : m.directive,
        segno: undefined,
        coda: undefined,
      }));
    if (!bars.length) return [];
    let meter: [number, number] = [4, 4];
    let chord: IRealChord | undefined;
    for (let i = 0; i <= start; i += 1) {
      meter = measures[i].timeSig ?? meter;
      if (i < start) chord = measures[i].chords.at(-1) ?? chord;
    }
    bars[0] = {
      ...bars[0], timeSig: meter,
      ...(bars[0].chords.length || !chord ? {} : {
        chords: [{ ...cloneChords([chord])[0], beats: bars[0].pickupBeats ?? meter[0] }],
      }),
    };
    const expanded = flattenMeasures(bars, { asReturn });
    if (!asReturn) {
      // A coda's Fine is terminal, after its written repeats have completed.
      for (let i = expanded.length - 1; i >= 0; i -= 1) {
        if (/^\s*fine\b/i.test(expanded[i].directive ?? '')) return expanded.slice(0, i + 1);
      }
    }
    return expanded;
  };
  const out = ranges.flatMap(({ start, end }, i) =>
    span(start, wantsCoda && i === ranges.length - 1 ? exit - 1 : end, true),
  );
  if (wantsCoda) out.push(...span(target, measures.length - 1, false));
  return out;
}

/** Expand bar-repeats, repeats, endings and navigation into performance order. */
export function flattenMeasures(
  measures: IRealMeasure[],
  options: {
    asReturn?: boolean;
    /** Start at the return instructed by this WRITTEN bar's D.S./D.C. */
    returnFrom?: number;
  } = {},
): IRealMeasure[] {
  const resolved = resolveBarRepeats(measures);
  const out: IRealMeasure[] = [];
  const stack: RepeatFrame[] = [];

  const codaIndices = resolved.map((m, i) => (m.coda ? i : -1)).filter((i) => i >= 0);
  const toCodaIndex = codaIndices[0] ?? -1;
  let codaTargetIndex = codaIndices.length > 1 ? codaIndices[codaIndices.length - 1] : -1;
  // A chart with a To Coda and a "D.S./D.C. al Coda" but NO second coda sign
  // (the 500 Miles High openbook source leaves its target commented out next
  // to "fix the coda thing") would otherwise play straight through the exit
  // on the D.S. pass. Take the bars after the navigation bar as the coda —
  // the same inference analyzeForm reports.
  if (codaTargetIndex < 0 && toCodaIndex >= 0) {
    const navIndex = resolved.findIndex(
      (m) => /D\.\s*[SC]\./i.test(m.directive ?? '') && /coda/i.test(m.directive ?? ''),
    );
    if (navIndex >= 0 && navIndex + 1 < resolved.length && toCodaIndex <= navIndex) {
      codaTargetIndex = navIndex + 1;
    }
  }
  // A D.S. with no written segno returns to the top of the form — after a
  // leading pickup (the anacrusis belongs to the first head only). The
  // openbook Misty writes exactly this.
  const segnoWritten = resolved.findIndex((m) => m.segno);
  const segnoIndex = segnoWritten >= 0 ? segnoWritten : resolved[0]?.pickupBeats != null ? 1 : 0;
  let armedToCoda = false;
  let stopAtFine = false;
  // After a D.C./D.S. return, repeats are NOT taken and only the LAST
  // alternative plays (the classical convention — Waltz For Debby's da capo
  // used to replay its |:A:| voltas and balloon the reading to 114 bars).
  // The To Coda jump CLEARS it: repeats inside the coda are literal again
  // (a "2nd time, rit." tag really plays twice). `asReturn` starts the
  // reading already returned: a head out read "to the ⊕" is the D.S. pass.
  let afterJump = options.asReturn ?? false;
  let returnEnding: number | null = null;
  // A D.C./D.S. is taken once; without this, a directive with no coda pair or
  // Fine to stop at jumps back forever (until the guard) on every pass.
  const takenDirectives = new Set<number>();
  // A `}` with no `{` before it repeats from the top (or from the previous
  // `}`) — standard notation and iReal's reading; Freddie Freeloader writes
  // `[ … N1 … } N2 … Z` and used to play both endings back to back. It opens
  // its frame once, on the first arrival; the frame then counts the passes.
  const takenImplicitRepeats = new Set<number>();
  let pc = 0;
  let guard = 0;

  const takeReturn = (index: number): boolean => {
    const dir = resolved[index]?.directive ?? '';
    const navigation = NAVIGATION.exec(dir);
    if (!navigation) return false;
    takenDirectives.add(index);
    const named = namedSectionReturn(resolved, dir);
    if (named) {
      out.push(...named);
      pc = resolved.length;
      return true;
    }
    armedToCoda = /\bcoda\b/i.test(dir);
    const ending = /\bal\s+(\d+)(?:st|nd|rd|th)?\s+end(?:ing)?\b/i.exec(dir);
    returnEnding = ending ? Number(ending[1]) : null;
    // "D.C. al 1st/2nd ending" names its alternative and ends at the
    // written Fine there, even though the directive does not repeat "Fine".
    stopAtFine = /\bfine\b/i.test(dir) || returnEnding !== null;
    afterJump = true;
    // A navigation return leaves the current repeat, even when the D.S.
    // is written inside it. Its old frame must not capture a later :|.
    stack.length = 0;
    takenImplicitRepeats.clear();
    pc = navigation[1].toUpperCase() === 'S' ? segnoIndex : 0;
    return true;
  };

  if (options.returnFrom != null) {
    const source = measures[options.returnFrom];
    // Similes may expand into two bars: their closing directive is carried
    // by the second one, so match both the written id and navigation text.
    const index = resolved.findIndex(
      (m) => m.id === source?.id && NAVIGATION.test(m.directive ?? ''),
    );
    if (index >= 0) takeReturn(index);
  }

  while (pc < resolved.length && guard++ < GUARD_LIMIT) {
    // "To Coda" jump (armed by a prior D.C./D.S. al Coda). The sign sits at
    // the START of the marked bar, so that bar is NOT played — iReal's manual:
    // "plays until the first coda symbol, then jumps".
    if (armedToCoda && pc === toCodaIndex && codaTargetIndex >= 0) {
      armedToCoda = false;
      afterJump = false;
      returnEnding = null;
      stack.length = 0;
      takenImplicitRepeats.clear();
      pc = codaTargetIndex;
      continue;
    }
    const m = resolved[pc];
    // A jump back to a frame's first bar re-enters it: the frame is still on
    // the stack and tracks the pass count.
    const entered = stack[stack.length - 1];
    const reentry = entered?.start === pc;

    // Post-return volta rule: an earlier alternative is skipped when a later
    // one follows (the return plays straight to the last ending). The coda
    // check above runs FIRST, so a To Coda inside volta 1 (Body And Soul)
    // still takes its jump.
    if (afterJump && m.ending != null) {
      const next = skipVolta(resolved, pc, []);
      if (
        returnEnding !== null
          ? m.ending !== returnEnding
          : resolved[next]?.ending === m.ending + 1
      ) {
        pc = next;
        continue;
      }
    }

    // Volta: skip a bracket that does not belong to the current pass. A
    // bracket that opens a repeat of its own (God Only Knows' 2nd ending is a
    // repeated instrumental) answers to the ENCLOSING repeat, once, before its
    // own frame opens; that frame's later passes come back here and play it.
    const bracketOutside = m.ending != null && stack.length > 0 && !reentry;
    if (m.ending != null && stack.length && !(reentry && entered.bracketOutside)) {
      const pass = stack[stack.length - 1].count + 1;
      if (m.ending !== pass) {
        pc = skipVolta(resolved, pc, stack);
        continue;
      }
      // The block's last bracket ends it as it begins: nothing in it jumps
      // back, and a frame left on the stack would claim a later bracket or
      // `}` (a D.C. al 3rd ending read as pass 2 of the finished repeat).
      const top = stack[stack.length - 1];
      if (pass >= Math.min(top.passes, top.lastBracket)) stack.pop();
    }

    // Open a repeat frame only on first entry — not when we jump back to it.
    if (m.open === 'repeat' && !afterJump && !reentry) {
      stack.push({
        start: pc,
        count: 0,
        ...passesFor(resolved, pc, bracketOutside),
        ...(bracketOutside ? { bracketOutside } : {}),
      });
    }

    out.push(m);

    // The Fine that is actually reached owns the stop. Another head/solo
    // region or an earlier volta can carry its own Fine on the same page.
    if (
      stopAtFine &&
      /\bfine\b/i.test(m.directive ?? '') &&
      !NAVIGATION.test(m.directive ?? '')
    ) break;

    if (!takenDirectives.has(pc) && takeReturn(pc)) continue;

    if (m.close === 'repeat' && !stack.length && !afterJump && !takenImplicitRepeats.has(pc)) {
      takenImplicitRepeats.add(pc);
      const start = implicitRepeatStart(resolved, pc);
      const plan = start >= 0 ? passesFor(resolved, start) : null;
      if (plan && plan.passes > 1) {
        stack.push({ start, count: 1, ...plan });
        pc = start;
        continue;
      }
    }
    if (m.close === 'repeat' && stack.length) {
      const top = stack[stack.length - 1];
      top.count += 1;
      if (top.count < top.passes) {
        pc = top.start;
        continue;
      }
      stack.pop();
    }
    pc += 1;
  }

  return out;
}

/**
 * Plan bare paired-coda charts for repeated playback. iReal commonly writes a
 * regular-chorus final between the first coda sign and a detached target. The
 * normal chorus stops there; only the final chorus takes the jump and tail.
 * Explicit D.C./D.S. navigation remains owned by the interpreter above.
 */
export function planPlaybackForm(measures: IRealMeasure[]): PlaybackFormPlan {
  const plan = planPlaybackPasses(measures);
  // iReal's `U` end marker ends the FINAL pass at its bar — material written
  // after it inside the form (a band break, a tag vamp) plays on every other
  // pass. `once` may hold several written passes of an inner repeat; the
  // marker's last occurrence is the tune's end.
  const endsAtMarker = honoursEndMarker(measures);
  if (!endsAtMarker) return { ...plan, endsAtMarker };
  // A bare |:…:| around the WHOLE form with the marker inside it is the
  // chorus loop the marker speaks about, not a written doubling: Cold Duck
  // Time is `{ 12 bars … Ebadd9 U<Fine> F7 | n | n }`, and reading the repeat
  // literally made ×1 play the break and go back to the top before ending at
  // the Fine the second time (user, Sep 2026: "still looping back to the
  // start when it hits the Fine"). The player's count drives the passes, so
  // one written pass is the chorus and the marker ends the last of them.
  // The same pairing rule the policy path collapses by; an `Nx` override on
  // the repeat keeps its literal count. It is the ONE such chart in the
  // Jazz 1460 — every other marker chart writes its loop without the pair.
  if (
    bareOuterRepeat(measures) &&
    !measures.some((m) => /(\d+)x/.test(m.staffText ?? ''))
  ) {
    const loopPass = flattenMeasures(dropOuterRepeat(measures));
    return { ...plan, loopPass, finalPass: cutAtEndMarker(loopPass), endsAtMarker };
  }
  return { ...plan, finalPass: cutAtEndMarker(plan.finalPass), endsAtMarker };
}

function planPlaybackPasses(measures: IRealMeasure[]): Omit<PlaybackFormPlan, 'endsAtMarker'> {
  const once = flattenMeasures(measures);
  // An "al Fine" ending is terminal: on repeated playback the loop passes
  // play the form straight through (no da capo), and only the final pass
  // takes the return and stops at Fine.
  const NAV_FINE = /D\.\s*[SC]\..*fine/i;
  if (measures.some((measure) => NAV_FINE.test(measure.directive ?? ''))) {
    const loopPass = flattenMeasures(
      measures.map((m) => (NAV_FINE.test(m.directive ?? '') ? { ...m, directive: undefined } : m)),
    );
    return { loopPass, finalPass: once, hasDetachedCoda: true };
  }
  if (measures.some((measure) => NAVIGATION.test(measure.directive ?? ''))) {
    return { loopPass: once, finalPass: once, hasDetachedCoda: false };
  }

  // Resolve similes before cutting paths so a target-side repeat can still copy
  // the authored bar immediately before it.
  const resolved = resolveBarRepeats(measures);
  const codas = resolved.flatMap((measure, index) => (measure.coda ? [index] : []));
  if (codas.length !== 2) return { loopPass: once, finalPass: once, hasDetachedCoda: false };
  const [toCoda, codaTarget] = codas;
  let coreFinal = -1;
  for (let i = codaTarget - 1; i >= toCoda; i -= 1) {
    if (resolved[i].close === 'final') {
      coreFinal = i;
      break;
    }
  }
  // A detached coda may follow a thin double bar rather than a final one.
  // Only the boundary directly before the marked target establishes this
  // ending; a double elsewhere can simply divide sections of the chorus.
  if (coreFinal < 0 && resolved[codaTarget - 1]?.close === 'double') {
    coreFinal = codaTarget - 1;
  }
  if (coreFinal < 0) return { loopPass: once, finalPass: once, hasDetachedCoda: false };

  const loopPass = flattenMeasures(resolved.slice(0, coreFinal + 1));
  // The sign sits at the start of the To Coda bar, so the final pass leaves
  // WITHOUT playing it — the coda replaces the core's tail.
  const finalPass = flattenMeasures([...resolved.slice(0, toCoda), ...resolved.slice(codaTarget)]);
  return { loopPass, finalPass, hasDetachedCoda: true };
}

/** Flatten a whole chart (optionally repeating the entire form). */
export function flattenChart(chart: IRealChart, options: FlattenOptions = {}): IRealMeasure[] {
  const once = flattenMeasures(chart.measures);
  const reps = Math.max(1, options.wholeRepeats ?? 1);
  if (reps === 1) return once;
  const out: IRealMeasure[] = [];
  for (let r = 0; r < reps; r++) {
    const pickupOnce = options.pickupOnce && r > 0 && once[0]?.pickupBeats != null;
    const pass = pickupOnce ? once.slice(1) : once;
    pass.forEach((m, index) =>
      out.push({
        ...m,
        id: uid('m'),
        chords: cloneChords(m.chords),
        ...(m.melody ? { melody: cloneMelody(m.melody) } : {}),
        // Omitting a pickup also omits the meter it normally establishes.
        // Restate that inherited meter on the first full bar so a mixed-meter
        // form resets correctly when the body comes round again.
        ...(pickupOnce && index === 0
          ? { timeSig: m.timeSig ?? once[0].timeSig ?? chart.timeSignature }
          : {}),
      }),
    );
  }
  return out;
}
